import { useState, useEffect } from 'react';
import { UserCheck, Plus, Eye, Copy, Archive, X, Check, Plus as PlusIcon, Trash2, User, Link as LinkIcon, Calendar, Pencil, Building2, ArrowUpRight, Phone, Mail, FileText, Euro, Search } from 'lucide-react';
import { Influencer, Platform, ContentRate, Collab, influencersRef, collabsRef, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';
import { Dialog } from '@headlessui/react';
import { cn } from '../lib/utils';

const PLATFORM_ICONS = {
  Instagram: {
    icon: 'https://cdn.simpleicons.org/instagram',
    color: 'text-pink-500',
  },
  YouTube: {
    icon: 'https://cdn.simpleicons.org/youtube',
    color: 'text-red-500',
  },
  TikTok: {
    icon: 'https://cdn.simpleicons.org/tiktok',
    color: 'text-purple-500',
  },
  Twitch: {
    icon: 'https://cdn.simpleicons.org/twitch',
    color: 'text-violet-500',
  },
  Snapchat: {
    icon: 'https://cdn.simpleicons.org/snapchat',
    color: 'text-yellow-400',
  },
} as const;

const PLATFORMS = [
  { value: 'Instagram', label: 'Instagram' },
  { value: 'YouTube', label: 'YouTube' },
  { value: 'TikTok', label: 'TikTok' },
  { value: 'Twitch', label: 'Twitch' },
  { value: 'Snapchat', label: 'Snapchat' },
] as const;

const CONTENT_TYPES = [
  { value: 'post_instagram', label: 'Post Instagram' },
  { value: 'story_instagram', label: 'Story Instagram' },
  { value: 'reel_instagram', label: 'Réel Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'story_snapchat', label: 'Story Snapchat' },
  { value: 'twitch', label: 'Live Twitch' },
  { value: 'youtube', label: 'Intégration YouTube' },
  { value: 'ugc', label: 'UGC (User Generated Content)' },
] as const;

const PlatformIcon = ({ platform }: { platform: keyof typeof PLATFORM_ICONS }) => {
  const { icon, color } = PLATFORM_ICONS[platform] || { icon: '', color: 'text-gray-400' };
  return (
    <img 
      src={icon} 
      alt={platform}
      className={cn('w-4 h-4', color)}
    />
  );
};

export default function Influencers() {
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [campaigns, setCampaigns] = useState<Collab[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [sortedInfluencers, setSortedInfluencers] = useState<Influencer[]>([]);
  const [search, setSearch] = useState('');
  const [exclusiveInfluencers, setExclusiveInfluencers] = useState<Influencer[]>([]);
  const [nonExclusiveInfluencers, setNonExclusiveInfluencers] = useState<Influencer[]>([]);
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    isExclusive: false,
    address: '',
    phoneNumber: '',
    birthDate: '',
    siret: '',
    internalReferent: '',
    internalNotes: '',
    platforms: [] as Platform[],
    rates: [] as ContentRate[],
    proposedTo: [] as string[],
  });

  useEffect(() => {
    loadInfluencers();
    loadCampaigns();
  }, []);

  const loadInfluencers = async () => {
    try {
      setIsLoading(true);
      const data = await getDocuments<Influencer>(influencersRef);
      const activeInfluencers = data.filter(inf => !inf.archived);

      // Separate exclusive and non-exclusive influencers
      const filtered = activeInfluencers.filter(inf =>
        (inf.fullName || '').toLowerCase().includes(search.toLowerCase()) ||
        (inf.username || '').toLowerCase().includes(search.toLowerCase()) ||
        inf.platforms.some(p => (p.handle || '').toLowerCase().includes(search.toLowerCase()))
      );

      const exclusive = filtered
        .filter(inf => inf.isExclusive)
        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      
      const nonExclusive = filtered
        .filter(inf => !inf.isExclusive)
        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      
      setInfluencers(activeInfluencers);
      setExclusiveInfluencers(exclusive);
      setNonExclusiveInfluencers(nonExclusive);
    } catch (error) {
      console.error('Error loading influencers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInfluencers();
  }, [search]);

  const loadCampaigns = async () => {
    try {
      const data = await getDocuments<Collab>(collabsRef);
      setCampaigns(data);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const handleAddPlatform = () => {
    setFormData(prev => ({
      ...prev,
      platforms: [...prev.platforms, { name: 'Instagram', audience: '', handle: '' }],
    }));
  };

  const handleRemovePlatform = (index: number) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.filter((_, i) => i !== index),
    }));
  };

  const handlePlatformChange = (index: number, field: keyof Platform, value: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.map((platform, i) => 
        i === index ? { ...platform, [field]: field === 'name' ? value as Platform['name'] : value } : platform
      ),
    }));
  };

  const handleAddRate = () => {
    setFormData(prev => ({
      ...prev,
      rates: [...prev.rates, { type: 'post_instagram', price: '' }],
    }));
  };

  const handleRemoveRate = (index: number) => {
    setFormData(prev => ({
      ...prev,
      rates: prev.rates.filter((_, i) => i !== index),
    }));
  };

  const handleRateChange = (index: number, field: keyof ContentRate, value: string) => {
    setFormData(prev => ({
      ...prev,
      rates: prev.rates.map((rate, i) => 
        i === index ? { ...rate, [field]: field === 'type' ? value as ContentRate['type'] : value } : rate
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedInfluencer?.id) {
        await updateDocument(influencersRef, selectedInfluencer.id, {
          ...formData,
          updatedAt: new Date(),
        });
        setIsEditing(false);
      } else {
        await createDocument(influencersRef, {
          ...formData,
          archived: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Influencer);
        setIsAddModalOpen(false);
      }
      await loadInfluencers();
      resetForm();
    } catch (error) {
      console.error('Error saving influencer:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      username: '',
      isExclusive: false,
      address: '',
      phoneNumber: '',
      birthDate: '',
      siret: '',
      internalReferent: '',
      internalNotes: '',
      platforms: [],
      rates: [],
      proposedTo: [],
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (selectedInfluencer) {
      setFormData({
        fullName: selectedInfluencer.fullName || '',
        username: selectedInfluencer.username || '',
        isExclusive: selectedInfluencer.isExclusive || false,
        address: selectedInfluencer.address || '',
        phoneNumber: selectedInfluencer.phoneNumber || '',
        birthDate: selectedInfluencer.birthDate || '',
        siret: selectedInfluencer.siret || '',
        internalReferent: selectedInfluencer.internalReferent || '',
        internalNotes: selectedInfluencer.internalNotes || '',
        platforms: [...(selectedInfluencer.platforms || [])],
        rates: [...(selectedInfluencer.rates || [])],
        proposedTo: [...(selectedInfluencer.proposedTo || [])],
      });
      setIsEditing(true);
    }
  };

  const handleViewDetails = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setIsDetailModalOpen(true);
  };

  const handleDuplicate = async (influencer: Influencer) => {
    try {
      const { id, createdAt, updatedAt, ...influencerData } = influencer;
      await createDocument(influencersRef, {
        ...influencerData,
        platforms: influencerData.platforms.map(platform => ({
          ...platform,
          handle: `${platform.handle || ''}_copy`
        }))
      });
      await loadInfluencers();
    } catch (error) {
      console.error('Error duplicating influencer:', error);
    }
  };

  const handleArchive = async (influencer: Influencer) => {
    try {
      if (!influencer.id) return;
      await updateDocument(influencersRef, influencer.id, { archived: true });
      await loadInfluencers();
    } catch (error) {
      console.error('Error archiving influencer:', error);
    }
  };

  const formatDate = (date: Date | { seconds: number, nanoseconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getInfluencerCampaigns = (influencerName: string) => {
    return campaigns.filter(campaign => 
      campaign.influencers.some(inf => inf.fullName === influencerName)
    ).sort((a, b) => {
      if ('seconds' in b.date && 'seconds' in a.date) {
        return b.date.seconds - a.date.seconds;
      }
      return 0;
    });
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En attente', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Influenceurs</h1>
            <p className="text-gray-400">Gérez votre réseau d'influenceurs</p>
          </div>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un influenceur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-dark-50 border border-dark-200 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-blue"
          />
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un influenceur
        </button>
      </div>

      <div className="space-y-8">
        {/* Exclusive Influencers Section */}
        <div className="bg-dark-50 rounded-xl border border-dark-200 overflow-hidden">
          <div className="p-6 border-b border-dark-200 bg-accent-blue/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-blue flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Influenceurs en exclusivité</h2>
                  <p className="text-gray-400">{exclusiveInfluencers.length} influenceur(s) en exclusivité</p>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-200">
                <thead className="bg-dark-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Influenceur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Plateformes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Référent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Dernière modification</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {exclusiveInfluencers.map((influencer) => (
                  <tr 
                    key={influencer.id} 
                    className="hover:bg-dark-100/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent-blue flex items-center justify-center">
                          <UserCheck className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{influencer.fullName || ''}</span>
                          </div>
                          <div className="text-sm text-gray-400">
                            {influencer.platforms[0]?.handle ? `@${influencer.platforms[0].handle}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {influencer.platforms.map((platform, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            <PlatformIcon platform={platform.name} />
                            <span className="text-sm text-gray-400">{platform.audience || ''}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {influencer.internalReferent || 'Non assigné'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {formatDate(influencer.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(influencer)}
                        className="text-accent-blue hover:text-accent-blue/80 mx-2"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(influencer)}
                        className="text-accent-blue hover:text-accent-blue/80 mx-2"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleArchive(influencer)}
                        className="text-red-500 hover:text-red-400 mx-2"
                      >
                        <Archive className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Non-Exclusive Influencers Section */}
        <div className="bg-dark-50 rounded-xl border border-dark-200 overflow-hidden">
          <div className="p-6 border-b border-dark-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-dark-200 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Autres influenceurs</h2>
                  <p className="text-gray-400">{nonExclusiveInfluencers.length} influenceur(s)</p>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-200">
                <thead className="bg-dark-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Influenceur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Plateformes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Référent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Dernière modification</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {nonExclusiveInfluencers.map((influencer) => (
                  <tr 
                    key={influencer.id} 
                    className="hover:bg-dark-100/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-dark-200 flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{influencer.fullName || ''}</span>
                          </div>
                          <div className="text-sm text-gray-400">
                            {influencer.platforms[0]?.handle ? `@${influencer.platforms[0].handle}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {influencer.platforms.map((platform, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            <PlatformIcon platform={platform.name} />
                            <span className="text-sm text-gray-400">{platform.audience || ''}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {influencer.internalReferent || 'Non assigné'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {formatDate(influencer.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(influencer)}
                        className="text-accent-blue hover:text-accent-blue/80 mx-2"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(influencer)}
                        className="text-accent-blue hover:text-accent-blue/80 mx-2"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleArchive(influencer)}
                        className="text-red-500 hover:text-red-400 mx-2"
                      >
                        <Archive className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Details Modal */}
      {isDetailModalOpen && selectedInfluencer && !isEditing && (
        <Dialog
          open={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedInfluencer(null);
          }}
          className="fixed inset-0 z-10 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen p-4">
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedInfluencer(null);
                  }}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedInfluencer.fullName || ''}</h2>
                    <div className="flex items-center gap-2">
                      {selectedInfluencer.platforms[0]?.handle && (
                        <p className="text-gray-400">@{selectedInfluencer.platforms[0].handle}</p>
                      )}
                      {selectedInfluencer.isExclusive && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-blue/20 text-accent-blue border border-accent-blue/20">
                          Exclusif
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Informations personnelles</h3>
                    <div className="bg-dark-100 rounded-lg p-4 space-y-4">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span>Né(e) le {selectedInfluencer.birthDate || 'Non renseigné'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${selectedInfluencer.phoneNumber}`} className="hover:text-white">
                          {selectedInfluencer.phoneNumber || 'Non renseigné'}
                        </a>
                      </div>
                      <div className="flex items-start gap-2 text-gray-400">
                        <Mail className="w-4 h-4 mt-1" />
                        <div className="flex-1">
                          <p className="whitespace-pre-wrap">{selectedInfluencer.address || 'Non renseigné'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <FileText className="w-4 h-4" />
                        <span>SIRET : {selectedInfluencer.siret || 'Non renseigné'}</span>
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-white mb-4 mt-6">Informations internes</h3>
                    <div className="bg-dark-100 rounded-lg p-4 space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-400">Référent interne</dt>
                        <dd className="mt-1 text-sm text-white">{selectedInfluencer.internalReferent || 'Non assigné'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-400">Dernière modification</dt>
                        <dd className="mt-1 text-sm text-white">{formatDate(selectedInfluencer.updatedAt)}</dd>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Plateformes</h3>
                    <div className="space-y-4">
                      {selectedInfluencer.platforms.map((platform, index) => (
                        <div key={index} className="flex items-center gap-4 bg-dark-100 p-4 rounded-lg">
                          <div className="flex items-center gap-2">
                            <PlatformIcon platform={platform.name} />
                            <span className="text-sm font-medium text-white">{platform.name}</span>
                          </div>
                          <div className="text-sm text-gray-400">
                            {platform.handle && <span>@{platform.handle}</span>}
                            {platform.handle && platform.audience && ' • '}
                            {platform.audience && <span>{platform.audience}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Tarifs</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedInfluencer.rates.map((rate, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-dark-100 rounded-lg">
                        <span className="text-sm font-medium text-white">
                          {CONTENT_TYPES.find(type => type.value === rate.type)?.label || rate.type}
                        </span>
                        <span className="text-sm text-gray-400">{formatCurrency(rate.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Campagnes</h3>
                  <div className="space-y-4">
                    {getInfluencerCampaigns(selectedInfluencer.fullName || '').map((campaign) => (
                      <div key={campaign.id} className="bg-dark-100 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-accent-blue" />
                            <div>
                              <h4 className="text-white font-medium">{campaign.name}</h4>
                              <p className="text-sm text-gray-400">{campaign.brand}</p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCampaignStatus(campaign).className}`}>
                            {getCampaignStatus(campaign).label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(campaign.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <User className="w-4 h-4" />
                              <span>{campaign.manager}</span>
                            </div>
                          </div>
                          <div>
                            {campaign.influencers.find(inf => inf.fullName === selectedInfluencer.fullName)?.deliverables.map((deliverable, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm text-gray-400">
                                <span>{deliverable.type}</span>
                                <span>{formatCurrency(deliverable.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    {getInfluencerCampaigns(selectedInfluencer.fullName || '').length === 0 && (
                      <div className="text-center py-6 bg-dark-100 rounded-lg">
                        <p className="text-gray-400">Aucune campagne pour cet influenceur</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedInfluencer.internalNotes && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Notes internes</h3>
                    <div className="bg-dark-100 rounded-lg p-4">
                      <p className="text-sm text-gray-400 whitespace-pre-wrap">{selectedInfluencer.internalNotes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {/* Add/Edit Modal */}
      <Dialog
        open={isAddModalOpen || (isDetailModalOpen && isEditing)}
        onClose={() => {
          if (isAddModalOpen) setIsAddModalOpen(false);
          if (isDetailModalOpen && isEditing) {
            setIsEditing(false);
            resetForm();
          }
        }}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => {
                  if (isAddModalOpen) setIsAddModalOpen(false);
                  if (isDetailModalOpen && isEditing) {
                    setIsEditing(false);
                    resetForm();
                  }
                }}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {isEditing ? 'Modifier l\'influenceur' : 'Ajouter un influenceur'}
                </h2>

                <div className="space-y-8">
                  {/* Basic Information */}
                  <div className="bg-dark-100 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-accent-blue" />
                      Informations de base
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-200">
                          Nom complet
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-200">
                          Nom d'utilisateur
                        </label>
                        <input
                          type="text"
                          id="username"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="isExclusive"
                          checked={formData.isExclusive}
                          onChange={(e) => setFormData({ ...formData, isExclusive: e.target.checked })}
                          className="h-4 w-4 rounded border-dark-300 text-accent-blue focus:ring-accent-blue"
                        />
                        <label htmlFor="isExclusive" className="text-sm font-medium text-gray-200">
                          En exclusivité avec l'agence
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="bg-dark-100 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-accent-blue" />
                      Informations personnelles
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-200">
                          Adresse
                        </label>
                        <textarea
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-200">
                          Numéro de téléphone
                        </label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="birthDate" className="block text-sm font-medium text-gray-200">
                          Date de naissance
                        </label>
                        <input
                          type="date"
                          id="birthDate"
                          value={formData.birthDate}
                          onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="siret" className="block text-sm font-medium text-gray-200">
                          SIRET
                        </label>
                        <input
                          type="text"
                          id="siret"
                          value={formData.siret}
                          onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="internalReferent" className="block text-sm font-medium text-gray-200">
                          Référent interne
                        </label>
                        <input
                          type="text"
                          id="internalReferent"
                          value={formData.internalReferent}
                          onChange={(e) => setFormData({ ...formData, internalReferent: e.target.value })}
                          className="mt-1 block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Platforms */}
                  <div className="bg-dark-100 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-white flex items-center gap-2">
                        <LinkIcon className="w-5 h-5 text-accent-blue" />
                        Plateformes
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddPlatform}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Ajouter une plateforme
                      </button>
                    </div>
                    <div className="space-y-4">
                      {formData.platforms.map((platform, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <select
                            value={platform.name}
                            onChange={(e) => handlePlatformChange(index, 'name', e.target.value)}
                            className="block w-40 rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          >
                            {PLATFORMS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="@handle"
                            value={platform.handle}
                            onChange={(e) => handlePlatformChange(index, 'handle', e.target.value)}
                            className="block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          />
                          <input
                            type="text"
                            placeholder="Audience"
                            value={platform.audience}
                            onChange={(e) => handlePlatformChange(index, 'audience', e.target.value)}
                            className="block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePlatform(index)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rates */}
                  <div className="bg-dark-100 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-white flex items-center gap-2">
                        <Euro className="w-5 h-5 text-accent-blue" />
                        Tarifs
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddRate}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Ajouter un tarif
                      </button>
                    </div>
                    <div className="space-y-4">
                      {formData.rates.map((rate, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <select
                            value={rate.type}
                            onChange={(e) => handleRateChange(index, 'type', e.target.value)}
                            className="block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          >
                            {CONTENT_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            placeholder="Prix"
                            value={rate.price}
                            onChange={(e) => handleRateChange(index, 'price', e.target.value)}
                            className="block w-40 rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveRate(index)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Internal Notes */}
                  <div className="bg-dark-100 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-accent-blue" />
                      Notes internes
                    </h3>
                    <textarea
                      value={formData.internalNotes}
                      onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                      rows={4}
                      className="block w-full rounded-md bg-dark-200 border-dark-300 text-white focus:border-accent-blue focus:ring-accent-blue"
                      placeholder="Notes internes sur l'influenceur..."
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (isAddModalOpen) setIsAddModalOpen(false);
                      if (isDetailModalOpen && isEditing) {
                        setIsEditing(false);
                        resetForm();
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
                  >
                    <Check className="w-5 h-5" />
                    {isEditing ? 'Enregistrer les modifications' : 'Ajouter l\'influenceur'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}